<script setup lang="ts">
import BaseButton from '@/common/components/molecules/baseButton/BaseButton.vue';
import { setup } from '@/common/components/organisms/baseMenu/baseMenu.setup';
import BaseMenuLogo from '@/common/components/organisms/baseMenu/baseMenuLogo/BaseMenuLogo.vue';
import BaseMenuSearch from '@/common/components/organisms/baseMenu/baseMenuSearch/BaseMenuSearch.vue';
import { clickoutside as vClickOutside } from '@/common/directives/clickoutside';
import SimpleTextTooltip from '@/common/components/molecules/simpleTextTooltip/SimpleTextTooltip.vue';
import TutorialGuide from '@/help/components/tutorialGuide/TutorialGuide.vue';
import MenuSearch from '@/common/components/organisms/menuSearch/MenuSearch.vue';
import MyInfoPopup from './popups/myInfoPopup/MyInfoPopup.vue';
import UserSupportPopup from './popups/userSupportPopup/UserSupportPopup.vue';
import FloatingMenuPopup from './popups/floatingMenuPopup/FloatingMenuPopup.vue';
import MenuElement from './menuElement/MenuElement.vue';
import UserMenuElement from './menuElement/UserMenuElement.vue';
import SupportMenuElement from './menuElement/SupportMenuElement.vue';
import ChatbotMenuElement from './menuElement/ChatbotMenuElement.vue';
import RightPanelMenu from './rightPanelMenu/RightPanelMenu.vue';
import { BASE_MENU_VALUE, POPOVER_MENU_TYPES } from './baseMenu.define';

const {
  t,

  menuInfo,
  isMenuReady,
  dashboardMenuInfo,
  analysisBoardMenuInfo,
  myInfoMenuInfo,
  helpMenuInfo,

  productName,
  hasHelpMenuPermission,

  mainPanelInfo,
  hoveredMenuInfo,
  isMyInfoMenuClicked,
  isDragging,

  baseMenuRef,
  popupMenuRef,
  popupMenuStyle,
  tooltipMouseEvent,
  isBaseMenuHidden,
  menuListRef,

  shouldShowFloatingMenu,
  onlyTitleFloatingPopup,

  onMouseEventInTarget,
  onHoverMainCategory,
  onHoverHelpCategory,
  onLeavePopoverMenu,
  onClickMainCategory,
  onClickMyInfoCategory,
  onClickSupportCategory,
  onShrinkMainPanel,
  onHideFloatingMenu,
  onHideMyInfoPopup,
  onClickTitle,
  onClickSetting,
  onClickOutsideMainPanel,
} = setup();
</script>

<template>
  <aside
    v-click-outside="onClickOutsideMainPanel"
    class="base-menu-wrapper"
    @mouseleave="onHideFloatingMenu"
  >
    <nav
      ref="baseMenuRef"
      :class="[
        'base-menu',
        {
          expanded: mainPanelInfo.isMainPanelExpanded,
          hidden: isBaseMenuHidden,
          'is-sorting': isDragging,
        },
      ]"
    >
      <div class="base-menu-header">
        <base-menu-logo
          :product-name="productName"
          :is-expand="mainPanelInfo.isMainPanelExpanded"
          @click-title="onClickTitle"
        />
        <base-button
          tag="button"
          class="base-menu-header__slide-button"
          @click="onShrinkMainPanel"
          @mouseenter="onMouseEventInTarget"
          @mouseleave="onMouseEventInTarget"
        >
          <ev-icon icon="icon-left-panel" />
        </base-button>
        <simple-text-tooltip
          :mouse-event="tooltipMouseEvent"
          base-pos="bottom"
          :text="`${t('WORD.HIDE_MENU')}`"
        />
      </div>
      <div class="base-menu-search-wrapper">
        <base-menu-search />
      </div>
      <ul
        ref="menuListRef"
        class="base-menu__top"
        :style="{ visibility: isMenuReady ? undefined : 'hidden' }"
      >
        <template
          v-for="info in menuInfo"
          :key="info.value"
        >
          <menu-element
            v-if="info.value !== BASE_MENU_VALUE.SETTINGS && info.value !== BASE_MENU_VALUE.HELP"
            :menu-info="info"
            :dashboard-menu-list="dashboardMenuInfo[info.value]"
            :analysis-board-menu-list="analysisBoardMenuInfo[info.value]"
            :selected="mainPanelInfo.selectedCategory === info.value"
            :data-menu-value="info.value"
            @click="() => onClickMainCategory(info)"
            @hover="(evt) => onHoverMainCategory(evt, info)"
          />
        </template>
      </ul>
      <ul class="base-menu__bottom">
        <chatbot-menu-element />
        <support-menu-element
          v-if="hasHelpMenuPermission"
          :info="helpMenuInfo"
          :selected="mainPanelInfo.selectedCategory === helpMenuInfo.value"
          @hover="onHoverHelpCategory"
          @click="onClickSupportCategory"
          @mouseleave="() => onLeavePopoverMenu(POPOVER_MENU_TYPES.HELP)"
        />
        <user-menu-element
          v-if="!!myInfoMenuInfo"
          :info="myInfoMenuInfo"
          :selected="mainPanelInfo.selectedCategory === myInfoMenuInfo.value"
          @hover="onHoverHelpCategory"
          @click="onClickMyInfoCategory"
          @mouseleave="() => onLeavePopoverMenu(POPOVER_MENU_TYPES.SETTINGS)"
        />
      </ul>
      <user-support-popup
        :class="{
          expanded: mainPanelInfo.isMainPanelExpanded,
          shown: hoveredMenuInfo?.value === BASE_MENU_VALUE.HELP,
        }"
        :style="popupMenuStyle"
        @mouseleave="onHideFloatingMenu"
      />
      <my-info-popup
        v-click-outside="onHideMyInfoPopup"
        :class="{
          expanded: mainPanelInfo.isMainPanelExpanded,
          shown: hoveredMenuInfo?.value === BASE_MENU_VALUE.SETTINGS || isMyInfoMenuClicked,
        }"
        :is-fixed="isMyInfoMenuClicked"
        :style="popupMenuStyle"
        @click-setting="onClickSetting"
        @mouseleave="onHideFloatingMenu"
      />
      <floating-menu-popup
        ref="popupMenuRef"
        :class="{
          shown: shouldShowFloatingMenu,
          onlyTitle: onlyTitleFloatingPopup,
        }"
        :style="popupMenuStyle"
        :menu-info="hoveredMenuInfo"
        :dashboard-menu-list="dashboardMenuInfo[hoveredMenuInfo?.value ?? '']"
        :analysis-board-menu-list="analysisBoardMenuInfo[hoveredMenuInfo?.value ?? '']"
        @mouseleave="onHideFloatingMenu"
      />
    </nav>
    <right-panel-menu />
    <tutorial-guide />
  </aside>
  <menu-search />
</template>

<style lang="scss">
$scrollbar-thumb-size: 6px;
.base-menu-wrapper {
  display: flex;
  z-index: $on-base-sub-menu;
  flex-direction: row;
  height: 100%;
  background-color: var(--gnb-bg-color);
}
.base-menu {
  --app-list-padding-top: 6px;
  --shadow-color: rgb(0, 0, 0, 0.14);
  --tooltip-bg-color: #393939;

  display: flex;
  position: relative;
  flex-direction: column;
  width: var(--gnb-menu-width);
  height: 100%;
  box-shadow:
    0 1px 3px 0 rgb(0, 0, 0, 0.14),
    0 2px 1px -1px rgb(0, 0, 0, 0.12),
    0 1px 1px 0 rgb(0, 0, 0, 0.2);
  transition: width $animate-gnb;

  &__top {
    display: flex;
    box-sizing: border-box;
    position: relative; // 드래그 indicator(absolute)의 컨테이닝 블록
    flex: 1;
    flex-direction: column;
    padding: 0 var(--gnb-menu-item-padding-horizontal);
    padding-right: calc(var(--gnb-menu-item-padding-horizontal) - $scrollbar-thumb-size);
    overflow: hidden auto;
    scrollbar-gutter: stable;
    border-right: 1px solid var(--gnb-border-color);

    > li {
      display: flex;
      box-sizing: border-box;
      flex-direction: column;
      flex-grow: 0;
      flex-shrink: 0;
      width: var(--gnb-menu-item-content-width);
      min-width: var(--gnb-menu-item-content-width);
      max-width: var(--gnb-menu-item-content-width);
      padding: 0;
      margin: 0;

      // 확장형일 때 width를 100%로 변경
      .base-menu.expanded & {
        width: 100%;
        min-width: 0;
        max-width: none;
      }
    }

    &::-webkit-scrollbar {
      background-color: var(--gnb-bg-color) !important;
    }

    &::-webkit-scrollbar-track {
      background-color: var(--gnb-bg-color) !important;
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--gnb-bg-color) !important;
    }

    &:hover {
      background-color: var(--gnb-bg-color) !important;
      &::-webkit-scrollbar-thumb {
        background-color: var(--gnb-scrollbar-thumb-hover-color) !important;
      }
      &::-webkit-scrollbar-thumb:hover {
        background-color: var(--gnb-scrollbar-thumb-hover-active-color) !important;
      }
    }
  }

  &__bottom {
    display: flex;
    box-sizing: border-box;
    position: relative;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    padding: 8px 12px;

    &::before {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      width: 100%;
      height: 1px;
      background-color: rgb(var(--color-gray-00-00-rgb), 0.2);
      content: '';
    }

    > li {
      display: flex;
      box-sizing: border-box;
      flex-direction: column;
      flex-grow: 0;
      flex-shrink: 0;
      width: var(--gnb-menu-item-content-width);
      min-width: var(--gnb-menu-item-content-width);
      max-width: var(--gnb-menu-item-content-width);
      padding: 0;
      margin: 0;

      .base-menu.expanded & {
        width: 100%;
        min-width: 0;
        max-width: none;
      }
    }
  }

  > div:not(.menu-popup),
  > ul {
    z-index: $on-base-sub-menu;
    background-color: var(--gnb-bg-color);
  }

  &.expanded {
    width: var(--gnb-menu-width-expanded);
  }

  &.hidden {
    width: 0;
    overflow: hidden;
  }

  .base-menu-header {
    border-right: 1px solid var(--gnb-border-color);
  }

  .base-menu-search-wrapper {
    display: flex;
    box-sizing: border-box;
    padding: 0 12px 4px;
  }

  @include max-screen() {
    .base-menu-search-wrapper {
      padding: 0 8px 4px;
    }

    &__bottom {
      padding: 8px;
    }
  }

  .onlyTitle {
    .floating-menu-popup__inner {
      width: 100%;
    }
    .floating-menu-popup__title {
      padding: 12px 20px;
    }
  }
}
</style>
